import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rolloverAPI } from '../../api/endpoints';
import { FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './CreateRollover.css';

const CreateRollover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_private: true,
  });

  const isPunterPlus = user?.punter_plus || false;
  const isCreatorActive = user?.creator_subscription_active || false;

  useEffect(() => {
    if (!isPunterPlus) {
      toast.error('You need Punter+ to create rollover games');
      navigate('/subscription');
      return;
    }
    if (!isCreatorActive) {
      toast.error('You need an active creator subscription to create rollover games');
      navigate('/creator/dashboard');
    }
  }, [isPunterPlus, isCreatorActive, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      const response = await rolloverAPI.create(formData);
      toast.success('Rollover game created successfully!');
      navigate(`/rollover/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create rollover');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-rollover-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-rollover-card">
        <div className="card-header">
          <h1>Create Rollover Game</h1>
          <p className="card-subtitle">Create a sequential bet code challenge for your subscribers</p>
          <div className="badges">
            <span className="punter-badge">⭐ Punter+</span>
            {isCreatorActive && (
              <span className="creator-badge">🎯 Creator Mode</span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="create-rollover-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              placeholder="e.g., Weekend Rollover Challenge"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Describe your rollover challenge..."
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_private"
                checked={formData.is_private}
                onChange={handleChange}
              />
              <span>Private Rollover</span>
              <span className="checkbox-hint">Only your subscribers can view and join</span>
            </label>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Create Rollover'}
            </button>
          </div>
        </form>

        <div className="rollover-info">
          <h4>How Rollover Works</h4>
          <ul>
            <li>📝 Add bet codes one at a time</li>
            <li>✅ Each bet must be marked as WON before adding the next</li>
            <li>📊 Subscribers follow your rollover progress</li>
            <li>🏆 Complete the rollover when all bets are won</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateRollover;